import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import auctionService from "../services/auctionService";
import { getImageUrl } from "../utils/image";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

interface Auction {
  id: string;
  title: string;
  description: string;
  status: string;
  images?: string[];
}

const MyPendingAuctions: React.FC = () => {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [auctionToDelete, setAuctionToDelete] = useState<Auction | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const data = await auctionService.getMyPendingAuctions();
      setAuctions(data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch pending auctions. Please try again later.");
      console.error("Error fetching auctions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuctions();
  }, []);

  const handleEdit = (id: string) => {
    navigate(`/edit-auction/${id}`);
  };

  const handleDeleteClick = (auction: Auction) => {
    setAuctionToDelete(auction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!auctionToDelete) return;
    setDeleting(true);
    try {
      await auctionService.deleteItem(auctionToDelete.id);
      setDeleteDialogOpen(false);
      setAuctionToDelete(null);
      fetchAuctions();
    } catch (err) {
      setError("Failed to delete auction.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAuctionToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button variant="contained" onClick={fetchAuctions}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        My Pending Auctions
      </Typography>
      <Paper sx={{ p: 2, maxWidth: 800, mx: "auto" }}>
        {auctions.length === 0 ? (
          <Typography color="text.secondary" align="center">
            No pending auctions found.
          </Typography>
        ) : (
          <List>
            {auctions.map((item, idx) => (
              <React.Fragment key={item.id}>
                <ListItem>
                  {item.images && item.images.length > 0 ? (
                    <img
                      src={getImageUrl(item.images[0])}
                      alt={item.title}
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        marginRight: 16,
                        borderRadius: 4,
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/default-auction.png";
                      }}
                    />
                  ) : (
                    <img
                      src={getImageUrl()}
                      alt="No auction preview"
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        marginRight: 16,
                        borderRadius: 4,
                      }}
                    />
                  )}
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="bold">
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Status:{" "}
                          {item.status === "pending" && (
                            <span style={{ color: "orange" }}>
                              Pending Approval
                            </span>
                          )}
                          {item.status === "active" && (
                            <span style={{ color: "green" }}>Active</span>
                          )}
                          {item.status === "rejected" && (
                            <span style={{ color: "red" }}>Rejected</span>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleEdit(item.id)}
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/auction/${item.id}`, "_blank");
                      }}
                      type="button"
                      sx={{ mr: 1 }}
                    >
                      View
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick(item)}
                      disabled={deleting}
                    >
                      Delete
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                {idx < auctions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the auction "
            {auctionToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPendingAuctions;
